/**
 * Rastreamento por checkout (settings.tracking): Meta Pixel, GA4 e Google
 * Ads, configurados no builder — independentes do pixel da PLATAFORMA (que
 * segue vindo de /integrations/fbpixel).
 *
 * Nada é injetado em modo preview (?_preview=1): edição no builder não pode
 * disparar evento de anúncio.
 */
import { useEffect } from 'react';
import ReactPixel from 'react-facebook-pixel';
import { ICheckoutPageSettings } from '../../../../api/checkouts/types/checkout-page.type';
import { isPreviewMode } from './use-preview-bridge';

type Tracking = NonNullable<ICheckoutPageSettings['tracking']>;

declare global {
	interface Window {
		dataLayer?: unknown[];
		gtag?: (...args: unknown[]) => void;
	}
}

/** Config ativa — lida na hora do Purchase (Form/useSubmit). */
let activeTracking: Tracking | null = null;

const loadGtag = (firstId: string) => {
	if (window.gtag) return;
	const script = document.createElement('script');
	script.async = true;
	script.src = `https://www.googletagmanager.com/gtag/js?id=${firstId}`;
	document.head.appendChild(script);
	window.dataLayer = window.dataLayer || [];
	window.gtag = function gtag() {
		// eslint-disable-next-line prefer-rest-params
		window.dataLayer?.push(arguments);
	};
	window.gtag('js', new Date());
};

/**
 * `kind` separa as etapas do funil: 'checkout' marca visita + intenção
 * (InitiateCheckout/begin_checkout; Purchase com valor sai no submit);
 * 'confirmation' marca a compra concluída na própria página de obrigado
 * (PageView + Purchase/conversion) — o padrão de remarketing por etapa.
 */
export const useCheckoutTracking = (
	tracking?: Tracking,
	kind: 'checkout' | 'confirmation' | 'landing' = 'checkout',
	/** Valor da compra — enriquece o Purchase da página de obrigado. */
	value?: number
) => {
	useEffect(() => {
		if (!tracking || isPreviewMode()) return;
		const { metaPixelId, ga4Id, googleAdsId, googleAdsLabel } = tracking;
		if (!metaPixelId && !ga4Id && !googleAdsId) return;

		if (kind === 'checkout') activeTracking = tracking;

		if (metaPixelId) {
			// Convive com o pixel da plataforma: o fbq dispara para todos os
			// pixels inicializados.
			ReactPixel.init(metaPixelId);
			ReactPixel.pageView();
			if (kind === 'checkout') ReactPixel.track('InitiateCheckout');
			else if (kind === 'confirmation')
				ReactPixel.track('Purchase', {
					currency: 'BRL',
					...(value ? { value } : undefined)
				});
		}

		const googleId = ga4Id || googleAdsId;
		if (googleId) {
			loadGtag(googleId);
			if (ga4Id) {
				window.gtag?.('config', ga4Id);
				if (kind !== 'landing') {
					window.gtag?.(
						'event',
						kind === 'checkout'
							? 'begin_checkout'
							: 'purchase',
						kind === 'confirmation' && value
							? { currency: 'BRL', value }
							: undefined
					);
				}
			}
			if (googleAdsId) {
				window.gtag?.('config', googleAdsId);
				if (kind === 'confirmation') {
					window.gtag?.('event', 'conversion', {
						send_to: googleAdsLabel
							? `${googleAdsId}/${googleAdsLabel}`
							: googleAdsId,
						...(value
							? { currency: 'BRL', value }
							: undefined)
					});
				}
			}
		}
	}, [tracking, kind, value]);
};

/**
 * Compra confirmada — chamado no submit, antes do redirect. Os SDKs enviam
 * por beacon, então o replace da página não perde o evento na prática.
 */
export const trackPurchase = ({
	value,
	reference
}: {
	value: number;
	reference?: string;
}) => {
	const tracking = activeTracking;
	if (!tracking) return;

	if (tracking.metaPixelId) {
		ReactPixel.track('Purchase', { currency: 'BRL', value });
	}
	if (tracking.ga4Id) {
		window.gtag?.('event', 'purchase', {
			currency: 'BRL',
			value,
			transaction_id: reference
		});
	}
	if (tracking.googleAdsId) {
		window.gtag?.('event', 'conversion', {
			send_to: tracking.googleAdsLabel
				? `${tracking.googleAdsId}/${tracking.googleAdsLabel}`
				: tracking.googleAdsId,
			currency: 'BRL',
			value,
			transaction_id: reference
		});
	}
};
